import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import api from "../api/api";
import Table from "../components/Table";
import Pagination from "../components/Pagination";
import Select from "react-select";
import ConfirmModal from "../components/ConfirmModal";
import CommonModal from "../components/CommonModal";
import InputField from "../components/InputField";
import Button from "../components/Button";
 
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchBar from "../components/SearchBar";
 
const Product = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
 
  const [productName, setProductName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedUnits, setSelectedUnits] = useState([]);
 
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
 
  /* SUCCESS MODAL */
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
 
  /* MODALS */
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
 
  /* EDIT */
  const [isEditMode, setIsEditMode] = useState(false);
  const [editProductId, setEditProductId] = useState(null);
  const [editProductData, setEditProductData] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
 
  /* PAGINATION */
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [displayProducts, setDisplayProducts] = useState([]);
 
  const getToken = () => localStorage.getItem("token");
 
  /* ================= FETCH ================= */
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/products", {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setProducts(res.data?.products || []);
    } catch {
      setError("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  }, []);
 
  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get("/categories", {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setCategories(res.data?.categories || []);
    } catch {}
  }, []);
 
  const fetchUnits = useCallback(async () => {
    try {
      const res = await api.get("/units", {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setUnits(res.data?.units || []);
    } catch {}
  }, []);
 
  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchUnits();
  }, [fetchProducts, fetchCategories, fetchUnits]);
 
  /* ================= OPTIONS ================= */
  const unitOptions = useMemo(
    () => units.map((u) => ({ value: u.id, label: u.unit_name })),
    [units],
  );
 
  /* ================= PREFILL EDIT ================= */
  useLayoutEffect(() => {
    if (!editProductData || !categories.length || !units.length) return;
 
    const category = categories.find(
      (c) =>
        c.category_name.toLowerCase() ===
        editProductData.category_name.toLowerCase(),
    );
 
    setSelectedCategory(category ? String(category.id) : "");
 
    const selected = editProductData.units
      ?.map((u) => unitOptions.find((o) => o.label === u))
      .filter(Boolean);
 
    setSelectedUnits(selected || []);
    setProductName(editProductData.product_name);
  }, [editProductData, categories, units, unitOptions]);
 
  /* ================= SAVE ================= */
  const handleSaveProduct = async () => {
    if (!productName || !selectedCategory || selectedUnits.length === 0) {
      setError("All fields are required");
      return;
    }
 
    const payload = {
      product_name: productName,
      category_id: Number(selectedCategory),
      unit_ids: selectedUnits.map((u) => u.value),
    };
 
    try {
      if (isEditMode) {
        await api.put(`/products/${editProductId}`, payload, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        setSuccessMessage("Product updated successfully");
      } else {
        await api.post("/products", payload, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        setSuccessMessage("Product added successfully");
      }
 
      setShowSuccessModal(true);
      resetForm();
      fetchProducts();
    } catch {
      setError("Failed to save product");
    }
  };
 
  const resetForm = () => {
    setProductName("");
    setSelectedCategory("");
    setSelectedUnits([]);
    setIsEditMode(false);
    setEditProductId(null);
    setEditProductData(null);
    setShowModal(false);
    setError("");
  };
 
  /* ================= EDIT ================= */
  const handleEditClick = (product) => {
    setIsEditMode(true);
    setEditProductId(product.id);
    setEditProductData(product);
    setShowModal(true);
  };
 
  /* ================= DELETE ================= */
  const handleDeleteClick = (id) => {
    setSelectedProductId(id);
    setShowConfirm(true);
  };
 
  const confirmDelete = async () => {
    try {
      await api.delete(`/products/${selectedProductId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setSuccessMessage("Product deleted successfully");
      setShowSuccessModal(true);
      fetchProducts();
    } catch {
      setError("Failed to delete product");
    } finally {
      setShowConfirm(false);
    }
  };
 
  const capitalizeFirstLetter = (v) =>
    v ? v.charAt(0).toUpperCase() + v.slice(1) : "";
 
  /* ================= FILTER & PAGINATION ================= */
  const filteredProducts = useMemo(
    () =>
      products.filter((p) =>
        p.product_name.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [products, searchTerm],
  );
 
  const totalItems = filteredProducts.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
 
  // const paginatedData = useMemo(() => {
  //   const start = (currentPage - 1) * itemsPerPage;
  //   return filteredProducts.slice(start, start + itemsPerPage);
  // }, [filteredProducts, currentPage, itemsPerPage]);
 
  /* ================= TABLE ================= */
  const columns = [
    { label: "Product Name", accessor: "product_name" },
    { label: "Categories", accessor: "category_name" },
    { label: "Units", render: (row) => row.units?.join(", ") },
    {
      label: "Actions",
      render: (row) => (
        <div className="d-flex gap-2 justify-content-center">
          <EditIcon
            sx={{ cursor: "pointer", color: "#6b7280" }}
            onClick={() => handleEditClick(row)}
          />
          <DeleteIcon
            sx={{ cursor: "pointer", color: "#6b7280" }}
            onClick={() => handleDeleteClick(row.id)}
          />
        </div>
      ),
    },
  ];
 
  return (
    <>
      {error && <div className="alert alert-danger">{error}</div>}
      <SearchBar
        data={products}
        searchKey="product_name"
        onFilteredData={setDisplayProducts}
        onAddClick={() => setShowModal(true)}
        placeholder="Search Product..."
        buttonLabel="+ Add Product"
      />
 
      <Table
        columns={columns}
        data={displayProducts}
        loading={loading}
        emptyMessage="No products found"
        pagination={
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(v) => {
              setItemsPerPage(v);
              setCurrentPage(1);
            }}
          />
        }
      />
 
      {/* ADD / EDIT MODAL */}
      {showModal && (
        <>
          <div className="modal fade show d-block bg-dark bg-opacity-50">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content p-4">
                <h5>{isEditMode ? "Edit Product" : "Add Product"}</h5>
 
                <label className="form-label mt-3">Category</label>
                <select
                  className="form-select mb-3"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">Select Category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.category_name}
                    </option>
                  ))}
                </select>
 
                <InputField
                  label="Product Name"
                  placeholder="Add Product name"
                  value={productName}
                  onChange={(e) =>
                    setProductName(capitalizeFirstLetter(e.target.value))
                  }
                />
 
                <label className="form-label mt-3">Units</label>
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  blurResetsInput={false}
                  hideSelectedOptions={false}
                  options={unitOptions}
                  value={selectedUnits}
                  onChange={(opts) => setSelectedUnits(opts || [])}
                />
 
                <div className="d-flex justify-content-end gap-2 mt-4">
                  <Button
                    variant="secondary"
                    btncls="bg-secondary m-0"
                    onClick={resetForm}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSaveProduct} btncls="m-0">
                    {isEditMode ? "Update" : "Save"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
 
      {/* DELETE CONFIRM */}
      {showConfirm && (
        <ConfirmModal
          show={showConfirm}
          title="Delete Product"
          message="Are you sure you want to delete this product?"
          confirmText="Yes, Delete"
          onConfirm={confirmDelete}
          onCancel={() => setShowConfirm(false)}
        />
      )}
 
      {/* SUCCESS MODAL */}
      <CommonModal
        show={showSuccessModal}
        title="Success"
        onClose={() => setShowSuccessModal(false)}
        footer={
          <Button
            variant="primary"
            text="OK"
            onClick={() => setShowSuccessModal(false)}
            btncls="m-0"
          />
        }
      >
        <p>{successMessage}</p>
      </CommonModal>
    </>
  );
};
 
export default Product;